"use client";

import { FormEvent, useRef, useState } from "react";
import {
  resetTurnstile,
  TurnstileWidget,
} from "@/components/ui/turnstile-widget";

export function GoogleAdsConsentWithdrawalForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [message, setMessage] = useState("");
  const turnstileRef = useRef<HTMLDivElement | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("sending");
    setMessage("");
    const form = new FormData(event.currentTarget);
    const turnstileToken =
      event.currentTarget.querySelector<HTMLInputElement>(
        'input[name="cf-turnstile-response"]',
      )?.value ?? "";
    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken) {
      setStatus("error");
      setMessage("Please complete the verification check before submitting.");
      return;
    }
    try {
      const response = await fetch(
        "/api/privacy/google-ads-withdrawal/request",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: form.get("email"),
            website: form.get("website"),
            turnstileToken,
          }),
        },
      );
      const result = (await response.json()) as {
        message?: string;
        error?: string;
      };
      if (!response.ok) {
        setStatus("error");
        setMessage(
          result.error || "We could not send the link. Please try again.",
        );
        return;
      }
      setStatus("sent");
      setMessage(
        result.message ||
          "If we found a matching enquiry, we have sent a confirmation link.",
      );
    } catch {
      setStatus("error");
      setMessage("We could not send the link. Please try again.");
    } finally {
      resetTurnstile(turnstileRef.current);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="not-prose mt-4 rounded-lg border border-border bg-surface-muted p-4"
    >
      <label
        htmlFor="withdrawal-email"
        className="block text-sm font-semibold text-navy"
      >
        Work email used for your enquiry or walkthrough request
      </label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input
          id="withdrawal-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          className="min-h-[44px] flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm text-navy"
        />
        <div className="absolute -left-[10000px]" aria-hidden="true">
          <label htmlFor="withdrawal-website">Website</label>
          <input
            id="withdrawal-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={status === "sending"}
          className="min-h-[44px] rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-dark disabled:cursor-wait disabled:opacity-60"
        >
          {status === "sending" ? "Sending…" : "Send confirmation link"}
        </button>
      </div>
      <TurnstileWidget className="mt-3" widgetRef={turnstileRef} />
      {message ? (
        <p
          role="status"
          className={`mt-3 text-sm ${status === "error" ? "text-red-700" : "text-text-secondary"}`}
        >
          {message}
        </p>
      ) : null}
      <p className="mt-3 text-sm text-text-secondary">
        If you cannot use the email link, contact{" "}
        <a
          href="mailto:privacy@thesitebook.co.uk"
          className="font-medium text-accent underline hover:text-accent-dark"
        >
          privacy@thesitebook.co.uk
        </a>
        . We will verify your request before changing any data.
      </p>
    </form>
  );
}
